import { useState } from 'react';
import { useAuth } from '../store/authContext';
import { useUsage } from '../lib/hooks';

export default function Settings() {
  const { user, updateUser } = useAuth();
  const { usage } = useUsage();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <aside className="md:w-64 flex-shrink-0">
            <nav className="space-y-1">
              <TabButton active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
                Profile
              </TabButton>
              <TabButton active={activeTab === 'team'} onClick={() => setActiveTab('team')}>
                Team Members
              </TabButton>
              <TabButton active={activeTab === 'domain'} onClick={() => setActiveTab('domain')}>
                Sending Domain
              </TabButton>
              <TabButton active={activeTab === 'api'} onClick={() => setActiveTab('api')}>
                API Keys
              </TabButton>
              <TabButton active={activeTab === 'notifications'} onClick={() => setActiveTab('notifications')}>
                Notifications
              </TabButton>
              <TabButton active={activeTab === 'billing'} onClick={() => setActiveTab('billing')}>
                Billing & Plan
              </TabButton>
            </nav>

            {/* Plan Summary */}
            {usage && (
              <div className="mt-6 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 text-white">
                <h3 className="font-semibold mb-2">Current Plan</h3>
                <p className="text-2xl font-bold capitalize">{usage.plan}</p>
                <p className="text-sm text-blue-100 mt-2">
                  {usage.emails.unlimited ? '∞' : usage.emails.sent.toLocaleString()} / {usage.emails.limit.toLocaleString()} emails
                </p>
                <button className="mt-3 w-full py-2 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50">
                  Upgrade Plan
                </button>
              </div>
            )}
          </aside>

          {/* Content */}
          <div className="flex-1">
            {activeTab === 'profile' && <ProfileSettings user={user} onUpdate={updateUser} />}
            {activeTab === 'team' && <TeamSettings />}
            {activeTab === 'domain' && <DomainSettings />}
            {activeTab === 'api' && <APISettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
            {activeTab === 'billing' && <BillingSettings />}
          </div>
        </div>
      </main>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
        active
          ? 'bg-blue-50 text-blue-600'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

function ProfileSettings({ user, onUpdate }: { user: any; onUpdate: (data: any) => void }) {
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    company_name: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    // API call to update profile
    setTimeout(() => {
      onUpdate(formData);
      setIsSaving(false);
      alert('Profile updated successfully!');
    }, 500);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Settings</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Your company name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={user?.email}
            disabled
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 text-gray-500"
          />
          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function TeamSettings() {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Team Members</h2>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          Invite Member
        </button>
      </div>
      <div className="text-center text-gray-500 py-12">
        <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        <p>No team members yet. Invite your first team member to collaborate.</p>
      </div>
    </div>
  );
}

function DomainSettings() {
  const [domain, setDomain] = useState('');
  
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Sending Domain</h2>
      <p className="text-gray-600 mb-6">
        Configure your custom sending domain to improve email deliverability and brand recognition.
      </p>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
          <div className="flex gap-3">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="yourdomain.com"
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
              Add Domain
            </button>
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-4">DNS Records</h3>
          <div className="space-y-4">
            <DNSRecord type="SPF" name="@" value="v=spf1 include:vmail.com ~all" />
            <DNSRecord type="DKIM" name="vmail._domainkey" value="k=rsa; p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQ..." />
            <DNSRecord type="DMARC" name="_dmarc" value="v=DMARC1; p=none;" />
          </div>
        </div>
      </div>
    </div>
  );
}

function DNSRecord({ type, name, value }: { type: string; name: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-900">{type} Record</span>
        <button onClick={handleCopy} className="text-sm text-blue-600 hover:text-blue-700">
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="text-sm">
        <p className="text-gray-500">Name: <code className="bg-white px-2 py-1 rounded">{name}</code></p>
        <p className="text-gray-500 mt-1">Value: <code className="bg-white px-2 py-1 rounded block mt-1 break-all">{value}</code></p>
      </div>
    </div>
  );
}

function APISettings() {
  const apiKey = 'vmail_sk_live_xxxxxxxxxxxxxxxxxxxx';
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">API Keys</h2>
      <p className="text-gray-600 mb-6">
        Use your API key to integrate VSENDS with your applications and third-party services.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Secret Key</label>
          <div className="flex gap-3">
            <input
              type="password"
              value={apiKey}
              readOnly
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 font-mono text-sm"
            />
            <button
              onClick={handleCopy}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            ⚠️ Keep your API key secret. Do not share it or expose it in client-side code.
          </p>
        </div>

        <div className="border-t pt-6">
          <button className="px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100">
            Regenerate API Key
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationSettings() {
  const [notifications, setNotifications] = useState({
    emailReports: true,
    campaignCompleted: true,
    bounceAlerts: true,
    quotaWarnings: true,
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Notification Preferences</h2>
      <div className="space-y-4">
        <NotificationToggle
          label="Email Reports"
          description="Receive weekly and monthly performance reports"
          checked={notifications.emailReports}
          onChange={() => setNotifications({ ...notifications, emailReports: !notifications.emailReports })}
        />
        <NotificationToggle
          label="Campaign Completed"
          description="Get notified when a campaign finishes sending"
          checked={notifications.campaignCompleted}
          onChange={() => setNotifications({ ...notifications, campaignCompleted: !notifications.campaignCompleted })}
        />
        <NotificationToggle
          label="Bounce Alerts"
          description="Receive alerts when bounce rate exceeds threshold"
          checked={notifications.bounceAlerts}
          onChange={() => setNotifications({ ...notifications, bounceAlerts: !notifications.bounceAlerts })}
        />
        <NotificationToggle
          label="Quota Warnings"
          description="Get warned when approaching your plan limits"
          checked={notifications.quotaWarnings}
          onChange={() => setNotifications({ ...notifications, quotaWarnings: !notifications.quotaWarnings })}
        />
      </div>
    </div>
  );
}

function NotificationToggle({ label, description, checked, onChange }: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-4 border-b last:border-0">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <button
        onClick={onChange}
        className={`relative w-12 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}
      >
        <span
          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'left-7' : 'left-1'}`}
        />
      </button>
    </div>
  );
}

function BillingSettings() {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Billing & Subscription</h2>
      <div className="text-center text-gray-500 py-12">
        <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
        <p className="mb-4">Manage your subscription and billing information</p>
        <button className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
          View Billing Portal
        </button>
      </div>
    </div>
  );
}
